import { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

import { FormTypography } from "@/forms-design-system/FormTypography";

interface DeliveryMethodCardProps {
  selected: boolean;
  onSelect: () => void;
  icon: ReactNode;
  label: string;
  "data-test"?: string;
}

export const DeliveryMethodCard: FC<DeliveryMethodCardProps> = ({
  selected,
  onSelect,
  icon,
  label,
  "data-test": dataTest,
}) => {
  const theme = useTheme();

  if (!theme.palette.forms) {
    return null;
  }

  const isDarkMode = theme.palette.metadata?.mode === "dark";

  const backgroundColor = selected
    ? isDarkMode
      ? theme.palette.forms.surface.supershy
      : theme.palette.forms.surface.moderate
    : isDarkMode
      ? theme.palette.forms.surface.moderate
      : theme.palette.forms.surface.supershy;

  const borderColor = selected
    ? isDarkMode
      ? theme.palette.forms.border.intense
      : theme.palette.forms.border.brand
    : isDarkMode
      ? theme.palette.forms.border.supershy
      : theme.palette.forms.border.quiet;

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      data-test={dataTest}
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        py: { xs: 1.5, md: 2 },
        px: 2,
        minHeight: { xs: 44, md: 52 },
        width: "100%",
        borderRadius: 1,
        border: `1px solid ${borderColor}`,
        backgroundColor,
        color: theme.palette.forms.text.intense,
        cursor: "pointer",
        transition: theme.transitions.create(["border-color", "background-color"]),
        "&:hover": {
          borderColor: isDarkMode ? theme.palette.forms.border.intense : theme.palette.forms.border.brand,
        },
      }}
    >
      <Box sx={{ flexShrink: 0, display: "flex", color: theme.palette.forms.text.brand }}>{icon}</Box>
      <FormTypography variant="subtitle2" component="span" sx={{ whiteSpace: "nowrap" }}>
        {label}
      </FormTypography>
    </Box>
  );
};
